<template>
  <UIModal v-model="isOpen" close-button :close-on-click-outside="false" @closed="emit('closed')">
    <template #header>
      <h2 class="halloween-expedition-modal__title">{{ $t('HalloweenAvailableExpeditionsModalTitle') }}</h2>
    </template>

    <div class="halloween-expedition-modal__content">
      <div class="halloween-expedition-modal__expedition-list">
        <div
          v-for="expedition in expeditions"
          :key="expedition.id"
          :class="[
            'halloween-expedition-modal__expedition-item',
            `halloween-expedition-modal__expedition-item--${expedition.state}`
          ]"
          @click="handleClick(expedition.id)"
        >
          <div class="halloween-expedition-modal__item-image-wrapper">
            <img
              v-if="expedition.image"
              :src="expedition.image"
              :alt="expedition.title"
              class="halloween-expedition-modal__item-image"
            />
            <div v-else class="halloween-expedition-modal__item-placeholder">
              <span>?</span>
            </div>
          </div>

          <div class="halloween-expedition-modal__item-info">
            <h3 class="halloween-expedition-modal__item-title">
              {{ expedition.title }}
            </h3>

            <div class="halloween-expedition-modal__item-status">
              <div
                v-if="expedition.endsAt && !expedition.claimAvailable"
                class="halloween-expedition-modal__item-timer"
              >
                <UIHalloweenCountdownTimer :target="expedition.endsAt" />
                <span>{{ $t('HalloweenAvailableExpeditionsModalUntilCompete') }}</span>
              </div>

              <span v-else-if="expedition.claimAvailable">{{
                $t('HalloweenAvailableExpeditionsModalStatusCompleted')
              }}</span>

              <span v-else>{{ $t('comingSoon') }}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </UIModal>
</template>

<script setup lang="ts">
import type { ExpeditionExtendedState } from '~/types/halloween/expedition';
import UIHalloweenCountdownTimer from '~/components/Halloween/ui/UIHalloweenCountdownTimer.vue';

const halloween = useHalloweenStore();
const { t } = useI18n();

const isOpen = defineModel<boolean>({ default: false });
const emit = defineEmits(['closed']);

const expeditions = computed(() => {
  const forest = halloween.statefulUserExpeditions.find((e) => e.location === 'forest');
  const cave = halloween.statefulUserExpeditions.find((e) => e.location === 'cave');

  const buildItem = (id: 'forest' | 'cave' | 'sea', exp?: ExpeditionExtendedState) => ({
    id,
    title: id === 'forest' ? t('forest') : id === 'cave' ? t('cave') : t('seaExpedition'),
    image: id === 'forest' ? '/img/buildings/forest.png' : id === 'cave' ? '/img/buildings/cave.png' : null,
    endsAt: exp?.endsAt ?? null,
    claimAvailable: exp?.claimAvailable ?? false,
    state: exp ? (exp.claimAvailable ? 'completed' : 'in-progress') : 'locked'
  });

  return [buildItem('cave', cave), buildItem('forest', forest), buildItem('sea')];
});

const handleClick = (id: string) => {
  if (id === 'sea') return;
  halloween.handleExpeditionLocationSelect(id as 'forest' | 'cave');
  isOpen.value = false;
};
</script>

<style lang="scss" scoped>
.halloween-expedition-modal {
  &__title {
    font-family: 'PT Serif', serif;
    font-size: 32px;
    font-weight: bold;
    line-height: 30px;
    color: #ffffff;
    text-align: center;
    padding-left: 24px;
    padding-right: 24px;
    padding-bottom: 8px;

    @media (min-width: 640px) {
      padding-left: 48px;
      padding-right: 48px;
    }
  }

  &__content {
    font-family: 'PT Serif', serif;
    padding: 10px;

    @media (min-width: 640px) {
      padding: 30px;
    }
  }

  &__expedition-list {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 100%;
  }

  &__expedition-item {
    display: flex;
    height: 100px;
    border-radius: 12px;
    justify-content: space-between;
    overflow: hidden;
    cursor: pointer;

    &--completed {
      box-shadow: 0px 0px 20px 0px #49ddaa40;
      border: 2px solid #49ddaa;

      background: linear-gradient(0deg, rgba(13, 36, 47, 0.75), rgba(13, 36, 47, 0.75)),
        radial-gradient(98.59% 98.53% at 51.79% 1.47%, rgba(0, 131, 173, 0.15) 0%, rgba(0, 0, 0, 0) 100%);

      &:hover {
        background: linear-gradient(0deg, rgba(13, 36, 47, 0.75), rgba(13, 36, 47, 0.75)),
          radial-gradient(98.59% 98.53% at 51.79% 1.47%, rgba(0, 131, 173, 0.75) 0%, rgba(0, 0, 0, 0) 100%);
      }

      .halloween-expedition-modal__item-status {
        color: #49ddaa;
      }
    }

    &--in-progress {
      border: 1px solid #1e353e;

      background: linear-gradient(0deg, rgba(13, 36, 47, 0.75), rgba(13, 36, 47, 0.75)),
        radial-gradient(98.59% 98.53% at 51.79% 1.47%, rgba(0, 131, 173, 0.15) 0%, rgba(0, 0, 0, 0) 100%);

      &:hover {
        background: linear-gradient(0deg, rgba(13, 36, 47, 0.75), rgba(13, 36, 47, 0.75)),
          radial-gradient(98.59% 98.53% at 51.79% 1.47%, rgba(0, 131, 173, 0.75) 0%, rgba(0, 0, 0, 0) 100%);
      }

      .halloween-expedition-modal__item-status {
        color: #dedede;
      }
    }

    &--locked {
      border: 1px solid #3f4344;
      cursor: default;
      background: radial-gradient(59.02% 71.08% at 84.01% 85.82%, rgba(25, 27, 27, 0.5) 0%, rgba(37, 40, 41, 0.5) 100%),
        linear-gradient(0deg, rgba(53, 55, 56, 0.5), rgba(53, 55, 56, 0.5)),
        radial-gradient(98.59% 98.53% at 51.79% 1.47%, rgba(158, 167, 170, 0.15) 0%, rgba(0, 0, 0, 0) 100%);

      .halloween-expedition-modal__item-status {
        color: #dedede;
      }
    }
  }

  &__item-image-wrapper {
    flex-shrink: 0;
  }

  &__item-image {
    width: 100%;
    max-width: 140px;
    height: 100%;
    object-fit: cover;
    object-position: center;
    display: block;
  }

  &__item-placeholder {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-left: 30px;

    @media (max-width: 640px) {
      margin-left: 15px;
    }

    span {
      font-size: 70px;
      font-weight: bold;
      color: #ffffff;
    }
  }

  &__item-info {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: flex-end;
    padding: 20px;
    text-align: left;
    gap: 10px;
  }

  &__item-title {
    color: #ffffff;
    font-weight: 700;
    font-size: 20px;
    line-height: 1;
  }

  &__item-timer {
    display: inline-flex;
    align-items: baseline;
    gap: 4px;
    flex-wrap: wrap;
  }

  &__item-status {
    font-weight: 400;
    font-size: 18px;
    line-height: 1;
  }
}
</style>
